// La barra de arriba de la ficha de un producto — ADR-2026-10-06-la-ficha-ocupa-la-pantalla.
//
// Antes, al tocar Modificar, la tabla se achicaba a una columna a la izquierda y la ficha
// ocupaba el resto: la columna se quedaba con un tercio de la pantalla para mostrar
// nombres cortados y un punto de color, y la ficha quedaba apretada. Ahora la ficha ocupa
// todo el ancho y esta barra hace lo que hacía la columna: volver a la tabla, saber en
// cuál se está y pasar a otro. Las flechas van al anterior o al siguiente, como en
// Shopify y Wix; el selector salta a cualquiera, con su foto y su estado.
import { useEffect, useRef, useState } from 'react';
import { ArrowLeft, Check, ChevronDown, ChevronLeft, ChevronRight } from 'lucide-react';
import CachedImg from '../../components/CachedImg';
import type { ProductoDeLanzamiento } from './lanzamientosApi';
import type { EstadoDeFila, TonoDeEstado } from './estadoDeFila';

const PUNTO: Record<TonoDeEstado, string> = {
  listo: 'bg-emerald-500',
  falta: 'bg-amber-400',
  publicado: 'bg-brand-primary',
  publicando: 'bg-sky-500',
  error: 'bg-red-500',
};

function Miniatura({
  producto,
  tamano,
  fotoDe,
}: {
  producto: ProductoDeLanzamiento;
  tamano: string;
  fotoDe?: (p: ProductoDeLanzamiento) => string | null;
}) {
  // Sin fotos nuevas, la que tiene hoy en la tienda (ADR-2026-10-29-tienda-online-refleja-la-tienda).
  const url = producto.imagenes[0]?.thumbUrl || fotoDe?.(producto) || null;
  return (
    <span className={`${tamano} shrink-0 overflow-hidden rounded-lg bg-neutral-100`}>
      {url && <CachedImg src={url} alt="" className="h-full w-full object-cover" />}
    </span>
  );
}

export function BarraDeLaFicha({
  productos,
  actual,
  estadoDe,
  onElegir,
  onVolver,
  fotoDe,
  sinVolver = false,
}: {
  /** En el orden de la tabla: las flechas lo siguen. */
  productos: readonly ProductoDeLanzamiento[];
  actual: ProductoDeLanzamiento;
  estadoDe: (p: ProductoDeLanzamiento) => EstadoDeFila;
  onElegir: (id: string) => void;
  onVolver: () => void;
  /** La foto de respaldo de un producto sin fotos nuevas: la primera que tiene en la tienda. */
  fotoDe?: (p: ProductoDeLanzamiento) => string | null;
  /** Sin "Todos los productos" (la ficha de un producto abierto desde Tienda online). */
  sinVolver?: boolean;
}) {
  const [abierto, setAbierto] = useState(false);
  const caja = useRef<HTMLDivElement>(null);
  const i = productos.findIndex((p) => p.id === actual.id);
  const anterior = i > 0 ? productos[i - 1] : null;
  const siguiente = i >= 0 && i < productos.length - 1 ? productos[i + 1] : null;
  const estado = estadoDe(actual);

  useEffect(() => {
    if (!abierto) return;
    const fuera = (e: MouseEvent) => {
      if (caja.current && !caja.current.contains(e.target as Node)) setAbierto(false);
    };
    const tecla = (e: KeyboardEvent) => e.key === 'Escape' && setAbierto(false);
    document.addEventListener('mousedown', fuera);
    document.addEventListener('keydown', tecla);
    return () => {
      document.removeEventListener('mousedown', fuera);
      document.removeEventListener('keydown', tecla);
    };
  }, [abierto]);

  const flecha =
    'grid h-9 w-9 place-items-center rounded-xl bg-neutral-100 text-brand-black transition-colors hover:bg-neutral-200 disabled:cursor-not-allowed disabled:opacity-35';

  return (
    // `relative` acá y no en el selector, en el teléfono: la lista desplegable se mide contra la
    // card entera y baja con su mismo ancho.
    <div className="relative rounded-2xl border border-neutral-200 bg-surface-card shadow-sm">
      <div className="flex items-center gap-2 p-2">
      {/* Debajo de `md` no va: la flecha flotante del header ya vuelve a la lista con la
          ficha abierta, y eran tres "volver" en la misma pantalla
          (ADR-2026-10-16-el-detalle-del-lanzamiento-en-el-telefono). */}
      {/* En la ficha de la tienda tampoco: la flecha del título ya vuelve a Tienda online. */}
      {!sinVolver && (
        <>
          <button
            type="button"
            onClick={onVolver}
            className="hidden shrink-0 items-center gap-1.5 rounded-xl py-2 px-3 text-sm font-medium text-brand-primary transition-colors hover:bg-[rgb(var(--brand-primary-rgb)_/_0.07)] md:flex"
          >
            <ArrowLeft className="h-4 w-4" />
            <span className="hidden sm:inline">Todos los productos</span>
            <span className="sm:hidden">Volver</span>
          </button>

          <span className="hidden h-6 w-px bg-neutral-200 md:block" aria-hidden="true" />
        </>
      )}

      {/* Qué producto es y saltar a otro. En el teléfono va solo en su renglón: las flechas
          bajan a uno propio, con texto. */}
      <div ref={caja} className="min-w-0 flex-1 sm:relative">
        <button
          type="button"
          onClick={() => setAbierto((v) => !v)}
          aria-expanded={abierto}
          aria-haspopup="listbox"
          // Del ancho de su contenido y no de toda la barra: así el chevron queda pegado al
          // nombre, que es lo que abre, y no suelto en la otra punta.
          className="inline-flex max-w-full min-w-0 items-center gap-3 rounded-xl py-1.5 pl-2 pr-3 text-left transition-colors hover:bg-neutral-50"
        >
          <Miniatura producto={actual} tamano="h-9 w-9" fotoDe={fotoDe} />
          <span className="min-w-0">
            <span className="block truncate text-sm font-semibold text-brand-black">{actual.nombre}</span>
            <span className="flex items-center gap-1.5 text-xs text-neutral-500">
              <span className={`h-1.5 w-1.5 rounded-full ${PUNTO[estado.tono]}`} aria-hidden="true" />
              {estado.texto}
            </span>
          </span>
          <ChevronDown className={`h-4 w-4 shrink-0 text-neutral-400 transition-transform ${abierto ? 'rotate-180' : ''}`} />
        </button>

        {abierto && (
          <ul
            role="listbox"
            aria-label="Elegí un producto"
            className="absolute inset-x-0 top-full z-30 mt-2 max-h-80 overflow-y-auto rounded-2xl border border-neutral-200 bg-white p-1.5 shadow-xl sm:right-auto sm:w-[360px]"
          >
            {productos.map((p) => {
              const e = estadoDe(p);
              const esEste = p.id === actual.id;
              return (
                <li key={p.id}>
                  <button
                    type="button"
                    role="option"
                    aria-selected={esEste}
                    onClick={() => {
                      setAbierto(false);
                      if (!esEste) onElegir(p.id);
                    }}
                    className={`flex w-full items-center gap-3 rounded-xl py-2 px-2 text-left transition-colors ${
                      esEste ? 'bg-[rgb(var(--brand-primary-rgb)_/_0.07)]' : 'hover:bg-neutral-50'
                    }`}
                  >
                    <Miniatura producto={p} tamano="h-9 w-9" fotoDe={fotoDe} />
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-sm font-medium text-brand-black">{p.nombre}</span>
                      <span className="flex items-center gap-1.5 text-xs text-neutral-500">
                        <span className={`h-1.5 w-1.5 rounded-full ${PUNTO[e.tono]}`} aria-hidden="true" />
                        {e.texto}
                      </span>
                    </span>
                    {esEste && <Check className="h-4 w-4 shrink-0 text-brand-primary" />}
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </div>

      <div className="ml-auto hidden shrink-0 items-center gap-2 sm:flex">
        <span className="text-xs tabular-nums text-neutral-500">
          {i + 1} de {productos.length}
        </span>
        <button
          type="button"
          onClick={() => anterior && onElegir(anterior.id)}
          disabled={!anterior}
          aria-label="Producto anterior"
          title={anterior ? `Anterior: ${anterior.nombre}` : undefined}
          className={flecha}
        >
          <ChevronLeft className="h-4 w-4" />
        </button>
        <button
          type="button"
          onClick={() => siguiente && onElegir(siguiente.id)}
          disabled={!siguiente}
          aria-label="Producto siguiente"
          title={siguiente ? `Siguiente: ${siguiente.nombre}` : undefined}
          className={flecha}
        >
          <ChevronRight className="h-4 w-4" />
        </button>
      </div>
      </div>

      {/* Teléfono: pasar de producto en un renglón propio, con texto y el nombre de a dónde
          lleva. Dos flechas de 36px al lado del nombre no se leían como "hay más productos"
          (pedido de Maxi, ADR-2026-10-16-el-detalle-del-lanzamiento-en-el-telefono). */}
      {productos.length > 1 && (
        <div className="flex items-stretch border-t border-neutral-100 sm:hidden">
          <button
            type="button"
            onClick={() => anterior && onElegir(anterior.id)}
            disabled={!anterior}
            aria-label={anterior ? `Producto anterior: ${anterior.nombre}` : 'Producto anterior'}
            className="flex min-w-0 flex-1 items-center gap-2 rounded-bl-2xl py-2.5 pl-3 pr-2 text-left transition-colors active:bg-neutral-50 disabled:opacity-35"
          >
            <ChevronLeft className="h-4 w-4 shrink-0 text-brand-black" />
            <span className="min-w-0">
              <span className="block text-sm font-medium text-brand-black">Anterior</span>
              {anterior && <span className="block truncate text-[11px] text-neutral-500">{anterior.nombre}</span>}
            </span>
          </button>
          <span className="flex shrink-0 items-center px-2 text-xs tabular-nums text-neutral-400">
            {i + 1} de {productos.length}
          </span>
          <button
            type="button"
            onClick={() => siguiente && onElegir(siguiente.id)}
            disabled={!siguiente}
            aria-label={siguiente ? `Producto siguiente: ${siguiente.nombre}` : 'Producto siguiente'}
            className="flex min-w-0 flex-1 items-center justify-end gap-2 rounded-br-2xl py-2.5 pl-2 pr-3 text-right transition-colors active:bg-neutral-50 disabled:opacity-35"
          >
            <span className="min-w-0">
              <span className="block text-sm font-medium text-brand-primary">Siguiente</span>
              {siguiente && <span className="block truncate text-[11px] text-neutral-500">{siguiente.nombre}</span>}
            </span>
            <ChevronRight className="h-4 w-4 shrink-0 text-brand-primary" />
          </button>
        </div>
      )}
    </div>
  );
}
